import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Select,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react';

// Converts camelCase / snake_case keys to readable "Title Case" labels
const formatKey = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Renders the receiver's counterparty config as a read-only key-value panel.
// The config is polymorphic (FPS/SWIFT/CHAPS/WIRE/etc.) so we show all keys
// generically rather than extracting specific fields.
const BeneficiaryDetailsPanel = ({ details }) => {
  if (!details || typeof details !== 'object' || Object.keys(details).length === 0) return null;
  return (
    <Box
      mt={3}
      p={4}
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.200"
      borderRadius="md"
    >
      <Text
        fontSize="2xs"
        color="whiteAlpha.500"
        textTransform="uppercase"
        letterSpacing="wider"
        mb={3}
      >
        Beneficiary Details
      </Text>
      <VStack align="stretch" spacing={2}>
        {Object.entries(details).map(([key, value]) => (
          <Flex key={key} justify="space-between" gap={4}>
            <Text fontSize="xs" color="whiteAlpha.600" flexShrink={0}>
              {formatKey(key)}
            </Text>
            <Text
              fontSize="xs"
              color="white"
              fontFamily="mono"
              textAlign="right"
              wordBreak="break-all"
            >
              {typeof value === 'object' && value !== null
                ? JSON.stringify(value, null, 2)
                : String(value ?? '')}
            </Text>
          </Flex>
        ))}
      </VStack>
    </Box>
  );
};
import { useDispatch, useSelector } from 'react-redux';
import {
  getTransfers,
  createTransfer,
  processTransfer
} from '@store/actions/libraDashboardActions';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import { updateLibraTransfersReqData } from '@store/actions/reqDataActions';
import Pagination from '@components/Pagination';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DataTable from '@components/DataTable';
import {
  MdAdd,
  MdArrowForward,
  MdClose,
  MdPlayArrow
} from 'react-icons/md';
import { LIBRA_STATUS } from '@constants';
import { getTransfersColumns } from './tableColumns';
import { getCreateTransferFields } from './formConfigs';
import {
  TRANSFER_STATUS_FILTER_OPTIONS,
  TRANSFER_MODE_FILTER_OPTIONS,
  TRANSFER_FILTER_ALL
} from './constants';
import { formatCurrencyAmount } from '@utils/numberUtils';

export const TransfersTab = ({ selectedItem, onItemClick }) => {
  const dispatch = useDispatch();
  const { hasAuthority } = useAuthRole();
  const isBackofficeManager = hasAuthority(ROLES.BACKOFFICE_MANAGER_ROLE);

  // Redux state
  const transfers = useSelector((state) => state.libraDashboard.transfers);
  const transferActions = useSelector((state) => state.libraDashboard.transferActions);
  const libraConfigs = useSelector((state) => state.libraDashboard.libraConfigs);
  const reqData = useSelector((state) => state.reqData.libraDashboard.transfers);

  // Local state for tracking selected account in create transfer modal
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);

  const {
    pageNumber: currentPage = 0,
    pageSize = 10,
    statusFilter = TRANSFER_FILTER_ALL,
    modeFilter = TRANSFER_FILTER_ALL,
    senderFilter = TRANSFER_FILTER_ALL
  } = reqData;

  const handleStatusFilterChange = (value) => {
    dispatch(
      updateLibraTransfersReqData({ statusFilter: value, pageNumber: 0 })
    );
  };

  const handleModeFilterChange = (value) => {
    dispatch(
      updateLibraTransfersReqData({ modeFilter: value, pageNumber: 0 })
    );
  };

  const handleSenderFilterChange = (value) => {
    dispatch(
      updateLibraTransfersReqData({ senderFilter: value, pageNumber: 0 })
    );
  };

  const handleResetFilters = () => {
    dispatch(
      updateLibraTransfersReqData({
        statusFilter: TRANSFER_FILTER_ALL,
        modeFilter: TRANSFER_FILTER_ALL,
        senderFilter: TRANSFER_FILTER_ALL,
        pageNumber: 0
      })
    );
  };

  const isFilterClean =
    statusFilter === TRANSFER_FILTER_ALL &&
    modeFilter === TRANSFER_FILTER_ALL &&
    senderFilter === TRANSFER_FILTER_ALL;

  const {
    isOpen: isCreateModalOpen,
    onClose: closeCreateModal,
    onOpen: openCreateModal
  } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onClose: closeConfirmModal,
    onOpen: openConfirmModal
  } = useDisclosure();

  useEffect(() => {
    fetchTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, senderFilter, statusFilter, modeFilter, libraConfigs]);

  const fetchTransfers = () => {
    const accountIds =
      senderFilter && senderFilter !== TRANSFER_FILTER_ALL
        ? [senderFilter]
        : libraConfigs?.accounts?.map((a) => a.accountId) ?? [];
    if (accountIds.length === 0) return;

    const _now = new Date();
    const _from = new Date();
    _from.setDate(_from.getDate() - 30);

    const body = {
      account_ids: accountIds,
      from: _from.toISOString(),
      to: _now.toISOString(),
      sort_by: 'created_at',
      sort_dir: 'desc',
      page: currentPage,
      page_size: pageSize,
      ...(statusFilter && statusFilter !== TRANSFER_FILTER_ALL
        ? { statuses: [statusFilter] }
        : {}),
      ...(modeFilter && modeFilter !== TRANSFER_FILTER_ALL
        ? { modes: [modeFilter] }
        : {})
    };
    dispatch(getTransfers(body));
  };

  const handlePageChange = (page) => {
    dispatch(updateLibraTransfersReqData({ pageNumber: page - 1 }));
  };

  // Handle field changes to track selected account
  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'accountId') {
      setSelectedAccountId(value);
    }
  };

  // Renders the Beneficiary Details panel below the Create Transfer form fields.
  // Receives live formData from DynamicInputModal via the renderFooter prop.
  // Wrapped in useCallback so libraConfigs lookup doesn't re-run on every render.
  const renderBeneficiaryDetails = useCallback((formData) => {
    if (!formData?.bankAccountId) return null;
    const receiver = libraConfigs?.corporateBeneficiaries?.find(
      (b) => b.corporateBeneficiaryId === formData.bankAccountId
    );
    const details =
      receiver?.corporateBeneficiaryDetails ??
      receiver?.corporate_beneficiary_details ??
      null;
    return <BeneficiaryDetailsPanel details={details} />;
  }, [libraConfigs]);

  // Reset state when modal closes
  const handleCloseCreateModal = () => {
    setSelectedAccountId(null);
    closeCreateModal();
  };

  // Called when form is submitted - shows confirmation modal
  const handleFormSubmit = (formData) => {
    setPendingFormData(formData);
    closeCreateModal();
    openConfirmModal();
  };

  // Called when confirmation is confirmed
  const handleConfirmCreate = () => {
    if (!pendingFormData) return;

    const selectedAccount = libraConfigs?.accounts?.find(
      (account) => account.accountId === pendingFormData.accountId
    );

    const trimmedReferenceId = (pendingFormData.referenceId || '').trim();

    const reqBody = {
      accountId: pendingFormData.accountId,
      bankAccountId: pendingFormData.bankAccountId,
      amount: pendingFormData.amount,
      currency: selectedAccount?.currency || '',
      // Only include referenceId when provided — backend auto-generates otherwise.
      ...(trimmedReferenceId ? { referenceId: trimmedReferenceId } : {})
    };

    dispatch(
      createTransfer(reqBody, (response) => {
        closeConfirmModal();
        setPendingFormData(null);
        setSelectedAccountId(null);

        // Directly select the newly created transfer using full response
        handleItemClick(response);

        // Refresh list but selection is already set
        fetchTransfers();
      })
    );
  };


  // Called when confirmation is cancelled
  const handleCancelConfirm = () => {
    closeConfirmModal();
    setPendingFormData(null);
  };

  const handleProcessTransfer = (externalId) => {
    if (window.confirm('Are you sure you want to process this transfer?')) {
      dispatch(
        processTransfer(externalId, (updatedTransfer) => {
          dispatch(updateLibraTransfersReqData({
            ...reqData,
            pageNumber: 0
          }));
          handleItemClick(updatedTransfer);
          fetchTransfers();
        })
      );
    }
  };


  // Get item click handler with sidebar actions
  const handleItemClick = (transfer) => {
    const sidebarActions = [];

    if (transfer.status === LIBRA_STATUS.PROCESSING || transfer.status === LIBRA_STATUS.OPEN) {
      sidebarActions.push(
        <Button
          key="process"
          size="md"
          colorScheme="green"
          leftIcon={<MdPlayArrow />}
          w="full"
          onClick={() => handleProcessTransfer(transfer.externalId)}
          isLoading={transferActions.isLoading}
        >
          Process
        </Button>
      );
    }

    onItemClick(transfer, sidebarActions);
  };

  const createTransferFields = getCreateTransferFields({
    libraConfigs,
    selectedAccountId
  });

  // Compact horizontal "ticket" preview rendered under the Create Transfer
  // fields. Total height ~110px so it never crowds the form. Updates live
  // so the user can sanity-check sender → receiver and amount before submit.
  const renderTransferSummary = (formData) => {
    const sender = libraConfigs?.accounts?.find(
      (a) => a.accountId === formData.accountId
    );
    const receiver = libraConfigs?.corporateBeneficiaries?.find(
      (b) => b.corporateBeneficiaryId === formData.bankAccountId
    );
    const summaryCurrency = sender?.currency || '';
    const amountDisplay = formatCurrencyAmount(formData.amount, summaryCurrency);
    const referenceTrimmed = (formData.referenceId || '').trim();
    const autoReferenceHint = sender?.uniqueAccountReference
      ? `Auto-generated · prefix: ${sender.uniqueAccountReference}-`
      : 'Auto-generated on submit';

    return (
      <Box
        px={6}
        py={5}
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius="lg"
      >
        {/* Header row: label on the left, prominent amount on the right */}
        <Flex justify="space-between" align="baseline" mb={2}>
          <Text
            fontSize="sm"
            color="whiteAlpha.500"
            textTransform="uppercase"
            letterSpacing="wider"
            fontWeight="600"
          >
            Transfer summary
          </Text>
          <Text
            fontSize="4xl"
            lineHeight="1.1"
            isTruncated
            ml={2}
          >
            {amountDisplay || '—'}
          </Text>
        </Flex>

        {/* Sender → Receiver in a single horizontal row */}
        <Flex align="center" gap={2}>
          <Box flex="1" minW={0}>
            <Text
              fontSize="lg"
              color={sender ? 'white' : 'whiteAlpha.400'}
              fontWeight="500"
              lineHeight="1.3"
              isTruncated
            >
              {sender?.name || 'Pick a sender'}
            </Text>
            <Text
              fontSize="md"
              color="whiteAlpha.600"
              lineHeight="1.3"
              isTruncated
            >
              {sender ? `${sender.accountId} · ${sender.currency}` : '—'}
            </Text>
          </Box>
          <Icon
            as={MdArrowForward}
            boxSize={4}
            color="whiteAlpha.400"
            flexShrink={0}
          />
          <Box flex="1" minW={0} textAlign="right">
            <Text
              fontSize="lg"
              color={receiver ? 'white' : 'whiteAlpha.400'}
              fontWeight="500"
              lineHeight="1.3"
              isTruncated
            >
              {receiver?.accountName || 'Pick a receiver'}
            </Text>
            <Text
              fontSize="md"
              color="whiteAlpha.600"
              lineHeight="1.3"
              isTruncated
            >
              {receiver
                ? `${receiver.corporateBeneficiaryId} · ${receiver.currency}`
                : '—'}
            </Text>
          </Box>
        </Flex>

        {/* Reference footer */}
        <Box
          mt={4}
          pt={3}
          borderTop="1px solid"
          borderTopColor="whiteAlpha.200"
        >
          <Text
            fontSize="md"
            color="whiteAlpha.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={1}
          >
            Reference
          </Text>
          <Text
            fontSize="lg"
            color={referenceTrimmed ? 'whiteAlpha.800' : 'whiteAlpha.400'}
            fontFamily="mono"
            wordBreak="break-all"
          >
            {referenceTrimmed || autoReferenceHint}
          </Text>
        </Box>
      </Box>
    );
  };

  // Body of the Create Transfer confirmation modal: section header,
  // the live ticket preview (renderTransferSummary), and the irreversible-
  // action disclaimer. Closes over pendingFormData since this is only
  // ever rendered after handleFormSubmit has populated it.
  const getConfirmTransferSummary = () => (
    <VStack align="stretch" spacing={3} pb={2}>
      <Text color="white" fontWeight="600" fontSize="2xl">
        Review &amp; confirm transfer
      </Text>
      {renderTransferSummary(pendingFormData || {})}
      <Text fontSize="md" color="whiteAlpha.600">
        Once confirmed, the transfer will be queued for processing. This
        action cannot be undone.
      </Text>
    </VStack>
  );

  return (
    <Box position={'relative'} overflowX={'hidden'} flex={1}>
      <ModalLayout
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        size="2xl"
        scrollBehavior="inside"
      >
        <DynamicInputModal
          title="Create Transfer"
          onClose={handleCloseCreateModal}
          onSubmit={handleFormSubmit}
          fields={createTransferFields}
          isLoading={transferActions?.isLoading}
          onFieldChange={handleFieldChange}
          renderFooter={renderBeneficiaryDetails}
        />
      </ModalLayout>

      {/* Confirmation Modal */}
      <ModalLayout
        isOpen={isConfirmModalOpen}
        onClose={handleCancelConfirm}
        size="2xl"
      >
        <ConfirmModal
          handleSubmit={handleConfirmCreate}
          handleCancel={handleCancelConfirm}
          isLoading={transferActions.isLoading}
          prompt={getConfirmTransferSummary()}
        />
      </ModalLayout>

      <VStack align="stretch" spacing={4}>
        {/* Header with heading and actions */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="lg" color="colorPrimary">
            Transfers
          </Heading>
          {isBackofficeManager && (
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              size="sm"
              onClick={openCreateModal}
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
            >
              Create Transfer
            </Button>
          )}
        </Flex>
        {!transfers.isLoading && transfers?.data?.items?.length > 0 && (
          <Flex mt={4}>
            <Pagination
              totalPages={transfers?.data?.maxPages || 1}
              currentPage={currentPage + 1}
              onPageChange={handlePageChange}
            />
          </Flex>
        )}

        {/* Filter bar */}
        <HStack spacing={3} align="center" flexWrap="wrap" mb={3}>
          <Select
            value={senderFilter}
            onChange={(e) => handleSenderFilterChange(e.target.value)}
            size="sm"
            flex={1}
            minW="240px"
            bg="#414141"
            color="white"
            borderColor="transparent"
            borderRadius="xl"
            focusBorderColor="brand.400"
            aria-label="Filter by sender account"
          >
            <option value={TRANSFER_FILTER_ALL} style={{ background: '#414141' }}>
              All senders
            </option>
            {(libraConfigs?.accounts || []).map((account) => (
              <option
                key={account.accountId}
                value={account.accountId}
                style={{ background: '#414141' }}
              >
                {account.name} ({account.currency})
              </option>
            ))}
          </Select>
          <Select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            size="sm"
            w="160px"
            flexShrink={0}
            bg="#414141"
            color="white"
            borderColor="transparent"
            borderRadius="xl"
            focusBorderColor="brand.400"
            aria-label="Filter by status"
          >
            <option value={TRANSFER_FILTER_ALL} style={{ background: '#414141' }}>
              All
            </option>
            {TRANSFER_STATUS_FILTER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
                style={{ background: '#414141' }}
              >
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            value={modeFilter}
            onChange={(e) => handleModeFilterChange(e.target.value)}
            size="sm"
            w="150px"
            flexShrink={0}
            bg="#414141"
            color="white"
            borderColor="transparent"
            borderRadius="xl"
            focusBorderColor="brand.400"
            aria-label="Filter by mode"
          >
            <option value={TRANSFER_FILTER_ALL} style={{ background: '#414141' }}>
              All
            </option>
            {TRANSFER_MODE_FILTER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
                style={{ background: '#414141' }}
              >
                {option.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="whiteAlpha"
            color="whiteAlpha.800"
            leftIcon={<MdClose />}
            onClick={handleResetFilters}
            isDisabled={isFilterClean}
          >
            Reset
          </Button>
        </HStack>

        {/* Transfers Table */}
        <DataTable
          data={transfers?.data?.items || []}
          columns={getTransfersColumns(handleItemClick, selectedItem)}
          isLoading={transfers.isLoading}
          noDataFoundMessage="No transfers found"
          onRowClick={handleItemClick}
          selectedRow={selectedItem}
          rowKeyField="id"
        />
      </VStack>
    </Box>
  );
};
